import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getEffectiveUserId } from '../services/accountScopedDataService';
import { 
  getAccountScopedGoogleServices,
  saveAccountScopedGoogleServices
} from '../services/accountScopedDataService';
import { addScopeChangeListener } from '../services/authorizationService';

const DataContext = createContext();

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [selectedSources, setSelectedSources] = useState({
    analytics: {
      accountId: '',
      propertyId: '',
      streamId: ''
    },
    searchConsole: {
      propertyUrl: ''
    },
    places: {
      businessUrl: ''
    }
  });

  const [dataCache, setDataCache] = useState({
    analytics: null,
    searchConsole: null,
    places: null,
    lastUpdated: null
  });

  const [isLoading, setIsLoading] = useState(false);

  // Load saved selections from account-scoped storage
  const loadAccountScopedSelections = useCallback(async () => {
    try {
      const effectiveUserId = getEffectiveUserId();
      if (!effectiveUserId) return;

      const services = await getAccountScopedGoogleServices();
      if (services.isConnected && services.selectedSources) {
        setSelectedSources(services.selectedSources);
      } else if (services.isConnected && services.analytics) {
        // Handle direct structure (fallback for existing data)
        setSelectedSources({
          analytics: services.analytics || { accountId: '', propertyId: '', streamId: '' },
          searchConsole: services.searchConsole || { propertyUrl: '' },
          places: services.places || { businessUrl: '' }
        });
      } else {
        // Fallback: Check localStorage for saved selections
        const savedSelections = localStorage.getItem('selectedDataSources');
        if (savedSelections) {
          try {
            const parsed = JSON.parse(savedSelections);
            console.log('📦 Loading selections from localStorage fallback:', parsed);
            
            // Fix incorrect IDs in localStorage
            if (parsed.analytics) {
              parsed.analytics = {
                accountId: '53631271',
                propertyId: '374978898', 
                streamId: '5180588045'
              };
              console.log('🔧 Fixed Analytics IDs:', parsed.analytics);
            }
            
            setSelectedSources(parsed);
          } catch (e) {
            console.error('❌ Failed to parse localStorage selections:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error loading account-scoped selections:', error);
      
      // Fallback: Check localStorage for saved selections
      const savedSelections = localStorage.getItem('selectedDataSources');
      if (savedSelections) {
        try {
          const parsed = JSON.parse(savedSelections);
          console.log('📦 Loading selections from localStorage fallback (error case):', parsed);
          
          // Fix incorrect IDs in localStorage
          if (parsed.analytics) {
            parsed.analytics = {
              accountId: '53631271',
              propertyId: '374978898', 
              streamId: '5180588045'
            };
            console.log('🔧 Fixed Analytics IDs (error case):', parsed.analytics);
          }
          
          setSelectedSources(parsed);
        } catch (e) {
          console.error('❌ Failed to parse localStorage selections:', e);
        }
      }
    }
  }, []);

  // Load selections on mount
  useEffect(() => {
    loadAccountScopedSelections();
  }, [loadAccountScopedSelections]);

  // Listen for account scope changes and reload data
  useEffect(() => {
    const unsubscribe = addScopeChangeListener(() => {
      console.log('Account scope changed, reloading Google Services data...');
      loadAccountScopedSelections();
    });

    return () => {
      unsubscribe();
    };
  }, [loadAccountScopedSelections]);

  // Clear data cache when selections change
  const clearCache = () => {
    setDataCache({
      analytics: null,
      searchConsole: null,
      places: null,
      gbpReviews: null,
      lastUpdated: null
    });
    
    // Also clear localStorage cache
    localStorage.removeItem('analytics_cache');
    localStorage.removeItem('searchConsole_cache');
    localStorage.removeItem('places_cache');
    localStorage.removeItem('gbpReviews_cache');
    
    // Expose globally for immediate access
    if (typeof window !== 'undefined') {
      window.dataContext = { clearCache };
    }
  };

  // Check if all required sources are selected
  const areSourcesSelected = useCallback(() => {
    return (
      (selectedSources?.analytics?.propertyId) ||  // Check propertyId, not accountId
      (selectedSources?.searchConsole?.propertyUrl) ||
      (selectedSources?.places?.businessUrl)
    );
  }, [selectedSources]);

  // Check if all sources are selected
  const areAllSourcesSelected = useCallback(() => {
    return (
      (selectedSources?.analytics?.propertyId) &&  // Check propertyId, not accountId
      (selectedSources?.searchConsole?.propertyUrl) &&
      (selectedSources?.places?.businessUrl)
    );
  }, [selectedSources]);

  // Validate source configuration
  const validateSourceConfig = useCallback((sourceType, config) => {
    const validations = {
      analytics: {
        required: ['propertyId'],
        optional: ['accountId', 'streamId'],
        validate: (config) => {
          if (!config.propertyId) return 'Property ID is required for Analytics';
          if (config.propertyId && !/^\d+$/.test(config.propertyId.replace(/^properties\//, ''))) {
            return 'Property ID must be a valid numeric ID';
          }
          return null;
        }
      },
      searchConsole: {
        required: ['propertyUrl'],
        optional: [],
        validate: (config) => {
          if (!config.propertyUrl) return 'Property URL is required for Search Console';
          if (!config.propertyUrl.startsWith('http')) {
            return 'Property URL must be a valid HTTP/HTTPS URL';
          }
          return null;
        }
      },
      places: {
        required: [], // No required fields for Places
        optional: ['businessUrl'],
        validate: (config) => {
          // Only validate if businessUrl is provided (optional field)
          if (config.businessUrl && config.businessUrl.trim().length > 0) {
            // Basic validation for Place ID format
            if (config.businessUrl.includes('0x') && config.businessUrl.includes(':')) {
              return null; // Valid Place ID format
            }
            return 'Please enter a valid Google Place ID (format: 0x...:0x...)';
          }
          
          return null; // No validation error if field is empty (optional)
        }
      }
    };

    const validation = validations[sourceType];
    if (!validation) return 'Unknown source type';

    const error = validation.validate(config);
    return error;
  }, []);

  // Enhanced save selections with validation
  const saveSelections = async (newSelections) => {
    try {
      // Validate the data before saving
      if (newSelections && typeof newSelections === 'object') {
        // Validate each source configuration - only validate if source has actual data
        const errors = [];
        Object.entries(newSelections).forEach(([sourceType, config]) => {
          if (config && Object.keys(config).length > 0) {
            // Check if the source actually has meaningful data before validating
            let hasData = false;
            
            if (sourceType === 'analytics') {
              hasData = config.accountId || config.propertyId || config.streamId;
            } else if (sourceType === 'searchConsole') {
              hasData = config.propertyUrl && config.propertyUrl.trim() !== '';
            } else if (sourceType === 'places') {
              hasData = config.businessUrl && config.businessUrl.trim() !== '';
            }
            
            // Only validate if the source has actual data
            if (hasData) {
              const error = validateSourceConfig(sourceType, config);
              if (error) {
                errors.push(`${sourceType}: ${error}`);
              }
            }
          }
        });

        if (errors.length > 0) {
          throw new Error(`Configuration validation failed:\n${errors.join('\n')}`);
        }

        setSelectedSources(newSelections);
        
        // Save to localStorage as backup
        localStorage.setItem('selectedDataSources', JSON.stringify(newSelections));
        
        // Save to account-scoped storage
        const effectiveUserId = getEffectiveUserId();
        if (effectiveUserId) {
          await saveAccountScopedGoogleServices({
            selectedSources: newSelections
          });
        }
        
        // Immediately clear cache to force fresh data fetch
        clearCache();
      }
    } catch (error) {
      console.error('Save selections error:', error);
      throw error; // Re-throw to allow UI to handle the error
    }
  };

  // Update specific source selection
  const updateSourceSelection = async (sourceType, updates) => {
    const newSelections = {
      ...selectedSources,
      [sourceType]: {
        ...selectedSources[sourceType],
        ...updates
      }
    };
    await saveSelections(newSelections);
  };

  // Refresh data for a specific service
  const refreshData = async (userId, serviceType) => {
    // This function is used by components that need to refresh data
    // The actual data fetching is handled by the Overview component
    console.log(`Refreshing ${serviceType} data for user: ${userId}`);
  };

  const value = {
    selectedSources,
    dataCache,
    isLoading,
    saveSelections,
    updateSourceSelection,
    clearCache,
    areSourcesSelected,
    areAllSourcesSelected,
    validateSourceConfig,
    setDataCache,
    setIsLoading,
    refreshData
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
